// src/pages/super-admin/Tests/TestList.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CreateTest from "./CreateTest";
import EditTest from "./EditTest";
import {
  useListTestTypesQuery,
  useDeleteTestTypeMutation,
  type TestType,
} from "@/features/tests/services/testApi";
import { toast } from "sonner";

export default function TestList() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<TestType | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");

  const {
    data: tests = [],
    isLoading,
    error,
    isError,
  } = useListTestTypesQuery();
  const [deleteTest] = useDeleteTestTypeMutation();

  const filteredTests = tests.filter((test) => {
    const matchesSearch = test.testName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         test.testCategory.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "ALL" || test.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Use underscore prefix to indicate intentionally unused parameter
  const handleCreate = (_newTest: TestType) => {
    setCreateOpen(false);
  };

  // Use underscore prefix to indicate intentionally unused parameter
  const handleUpdate = (_updatedTest: TestType) => {
    setEditOpen(false);
    setSelectedTest(null);
  };

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this test type?")) {
      try {
        await deleteTest(id).unwrap();
        toast.success("Test type deleted successfully");
      } catch (err) {
        toast.error("Failed to delete test type");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-gray-500">Loading test types...</p>
      </div>
    );
  }

  if (isError || error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800 font-bold">Error loading test types</p>
        <p className="text-red-600 text-sm mt-2">{JSON.stringify(error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Test Types Management</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600">+ Add Test Type</Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Test Type</DialogTitle>
            </DialogHeader>
            <CreateTest
              onSuccess={handleCreate}
              onClose={() => setCreateOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search test name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 w-full md:w-48"
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      {/* Tests Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Tests ({filteredTests.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {tests.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-gray-500 font-medium mb-4 mt-4">
                No test types added yet
              </p>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600">+ Add First Test Type</Button>
                </DialogTrigger>
              </Dialog>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold">
                      Test Name
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">
                      Category
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">
                      Description
                    </th>
                    <th className="text-center px-4 py-3 font-semibold">
                      Status
                    </th>
                    <th className="text-center px-4 py-3 font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTests.map((test) => (
                    <tr key={test.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{test.testName}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {test.testCategory}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {test.testDescription}
                      </td>
                      <td className="text-center px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-sm font-medium inline-block ${
                            test.status === "ACTIVE"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {test.status}
                        </span>
                      </td>
                      <td className="text-center px-4 py-3 space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTest(test);
                            setEditOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(test.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Test Type</DialogTitle>
          </DialogHeader>
          {selectedTest && (
            <EditTest
              test={selectedTest}
              onSuccess={handleUpdate}
              onClose={() => {
                setEditOpen(false);
                setSelectedTest(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
